import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Award, Sun, Moon, Users, Sparkles, ChevronRight, Eye, Mic, Archive, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { fetchBuddies, fetchFlagshipProgram, fetchFlagshipProgramPreview, startFlagshipProgram, stepFlagshipDay, trackProgramEvent, mediaUrl, streakCopy, getProgramReminders, welcomeEvent } from '../lib/programsApi';
import { ProgramDayStrip } from '../components/programs/ProgramDayStrip';
import { MeditationStep, AffirmationStep, BreathingStep, EveningStep } from '../components/programs/ProgramStepCards';
import { ProgramJournalStep } from '../components/programs/ProgramJournalStep';
import { ProgramCommunityShare } from '../components/programs/ProgramCommunityShare';
import { ProgramCompletion } from '../components/programs/ProgramCompletion';
import { ProgramWelcome } from '../components/programs/ProgramWelcome';
import { ProgramReminderTimes } from '../components/programs/ProgramReminderTimes';
import { JourneyPostcard } from '../components/programs/JourneyPostcard';
import { JourneyInvite } from '../components/programs/JourneyInvite';
import { JourneyBuddies } from '../components/programs/JourneyBuddies';
import { WelcomeVideoCard } from '../components/programs/WelcomeVideoCard';

const ENCOURAGEMENTS = [
  'You showed up for yourself today. That is the whole practice.',
  'One more breath than yesterday. Small steps, bigger peace.',
  'You gave yourself a few quiet minutes — notice how that feels.',
  'Stillness is a skill, and you just practised it.',
  'Letting go gets lighter every time. Well done.',
  'You came back to yourself today. Carry that with you.',
  'Look how far you have come. A calmer you is a brighter tomorrow.',
];
const SERIF = { fontFamily: "'Playfair Display', serif" };

const SectionHead = ({ icon: Icon, label, hint }) => (
  <div className="flex items-baseline justify-between mt-8 mb-3">
    <h2 className="flex items-center gap-2 text-base font-bold text-[#f9f4ea] uppercase tracking-[0.2em]" style={SERIF}><Icon className="w-4 h-4 text-[#D6AD60]" /> {label}</h2>
    {hint && <span className="text-[11px] text-[#e3d2a8]/70">{hint}</span>}
  </div>
);

export default function FlagshipProgramPage({ slug }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [welcome, setWelcome] = useState(null); // null | 'first' | 'replay'
  const [postcard, setPostcard] = useState(false);
  const [invite, setInvite] = useState(false);
  const [buddies, setBuddies] = useState(null);
  const viewedRef = useRef(null);
  const location = useLocation();
  const isPreview = new URLSearchParams(location.search).get('preview') === '1';

  const load = useCallback(async () => {
    try {
      const d = await (isPreview ? fetchFlagshipProgramPreview(slug) : fetchFlagshipProgram(slug));
      setData(d);
      setError(null);
    } catch (e) { setError(e.status === 404 ? 'Program not found.' : 'Could not load this program.'); }
  }, [slug, user?.id, isPreview]);

  useEffect(() => { window.scrollTo(0, 0); load(); }, [load]);
  const loadBuddies = useCallback(() => { if (user && !isPreview) fetchBuddies(slug).then(setBuddies).catch(() => {}); }, [user, slug, isPreview]);
  useEffect(() => { loadBuddies(); }, [loadBuddies, data?.program?.status]);
  useEffect(() => {
    if (user && data && !isPreview && viewedRef.current !== slug) { viewedRef.current = slug; trackProgramEvent(slug, 'program_view'); }
  }, [user, data, slug, isPreview]);
  useEffect(() => {
    const p = data?.program;
    if (!p || isPreview || p.status !== 'IN_PROGRESS' || !data.welcome || p.welcomeOffered || (data.dayProgress || []).length) return;
    setWelcome('first');
    welcomeEvent(slug, 'offered');
  }, [data?.program?.attemptId, isPreview]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (location.hash !== '#tonight' || !data) return;
    const t = setTimeout(() => document.getElementById('tonight')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
    return () => clearTimeout(t);
  }, [location.hash, data]);

  const program = data?.program;
  const days = data?.days || [];
  const active = program?.status === 'IN_PROGRESS';
  const completed = program?.status === 'COMPLETED';
  const currentDay = active ? program.currentDay : completed ? program.totalDays : 0;
  const sel = selected || currentDay || 1;
  const day = days.find((d) => d.dayNumber === sel);
  const dpMap = useMemo(() => Object.fromEntries((data?.dayProgress || []).map((x) => [x.dayNumber, x])), [data]);
  const completedDays = useMemo(() => new Set(Object.values(dpMap).filter((x) => x.completed).map((x) => x.dayNumber)), [dpMap]);
  const dp = dpMap[sel];
  const entry = data?.journal?.[String(sel)];
  const locked = isPreview || !active || sel > currentDay;
  const guest = !user && !isPreview;
  const dayLabel = program?.dayLabel || 'Day';
  const streak = program?.consecutiveProgramDays || 0;
  const streakLine = streakCopy(streak);

  const act = useCallback(async (step, action, body) => {
    const wasDone = !!dpMap[sel]?.completed;
    try {
      const r = await stepFlagshipDay(slug, sel, step, action, body);
      setData((d) => ({
        ...d,
        dayProgress: [...(d.dayProgress || []).filter((x) => x.dayNumber !== sel), r.dayProgress],
        journal: r.journalEntry ? { ...d.journal, [String(sel)]: r.journalEntry } : d.journal,
        program: { ...d.program, currentDay: r.progress.currentDay, percentComplete: r.progress.percentComplete, status: r.progress.status,
          completedAt: r.progress.completedAt, completions: r.progress.completions, badgeEarned: d.program.badgeEarned || r.programCompleted,
          consecutiveProgramDays: r.progress.consecutiveProgramDays ?? d.program.consecutiveProgramDays },
      }));
      if (r.programCompleted) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        setExpanding(true);
        setTimeout(() => { setCelebrate(true); setExpanding(false); load(); }, reduce ? 0 : 1000);
      }
      else if (r.dayCompleted && !wasDone) toast.success(`${dayLabel} ${sel} complete`);
      return r;
    } catch (e) { toast.error(e.message || 'Could not save progress'); throw e; }
  }, [slug, sel, dpMap, dayLabel, load]);

  const liveRef = useRef({});
  liveRef.current = { day, dp, locked, act };
  useEffect(() => {
    const onEnded = (e) => {
      const s = liveRef.current;
      if (!s.day?.primary || s.locked || s.dp?.meditationComplete) return;
      if (e.detail?.contentId === s.day.primary.id) s.act('meditation', 'complete', { method: 'audio_end' }).catch(() => {});
    };
    window.addEventListener('rbr:audio-ended', onEnded);
    return () => window.removeEventListener('rbr:audio-ended', onEnded);
  }, []);

  const begin = async () => {
    if (isPreview) return toast('Admin preview — nothing is saved');
    if (!user) {
      // Programs are open to everyone — visitors walk the journey freely; only saved progress needs a profile.
      setSelected(1);
      setTimeout(() => document.getElementById('flagship-days')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      toast('Every day is open to you', { description: 'Sign in anytime to track your progress, streak and badge.' });
      return;
    }
    setBusy(true);
    try {
      await startFlagshipProgram(slug);
      await load();
      setSelected(1);
      setCelebrate(false);
      toast.success(completed ? 'A fresh start — your badge and history are kept' : 'Your journey begins');
      const rem = await getProgramReminders().catch(() => null);
      if (rem?.enabled && rem?.hasPush) toast('Program reminders are on', { description: "We'll gently remind you in the morning and evening. You can change this anytime." });
    } catch (e) { toast.error(e.message || 'Could not start'); } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-5" data-testid="flagship-error">
        <p className="text-stone-500 mb-4">{error}</p>
        <button onClick={() => navigate('/programs')} className="px-5 py-2.5 btn-gold rounded-full text-sm font-semibold">Back to programs</button>
      </div>
    );
  }
  if (!program) return <div className="px-5 pt-6"><div className="h-64 rounded-3xl bg-[#efe3ce] animate-pulse" /></div>;

  const lastCompletion = data.history?.[data.history.length - 1];
  const cover = program.coverImageUrl;

  return (
    <div className="animate-fade-in pb-12" data-testid={`flagship-program-${program.slug}`}>
      {isPreview && (
        <div className="bg-[#D6AD60] text-[#0f1e33] text-xs font-semibold px-5 py-2 flex items-center gap-2" data-testid="flagship-preview-banner">
          <Eye className="w-3.5 h-3.5" /> Admin preview · {program.status} · nothing you do here is saved
        </div>
      )}
      <div className="relative">
        <div className="aspect-[4/3] w-full overflow-hidden bg-[#1e3a5f]">
          {cover && <img src={mediaUrl(cover, 800)} alt={program.title} className="w-full h-full object-cover" />}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f1e33]/50 via-transparent to-transparent" />
        <button onClick={() => navigate('/programs')} className="absolute top-4 left-4 w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-black/60 transition-colors" data-testid="flagship-back">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="px-5 pt-5">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#e3d2a8] mb-1">{program.category} · {program.totalDays} {dayLabel}s</p>
        <h1 className="text-3xl font-bold text-[#f9f4ea] leading-tight mb-3" style={SERIF} data-testid="flagship-title">{program.title}</h1>
        <p className="text-sm text-[#e3d2a8]/90 leading-relaxed mb-5">{program.description}</p>

        {data.welcomeVideo && !isPreview && (
          <WelcomeVideoCard program={program} video={data.welcomeVideo}
            startLabel={program.status === 'NOT_STARTED' ? `Start ${dayLabel} 1` : completed ? `Revisit ${dayLabel} 1` : `Go to ${dayLabel} ${currentDay}`}
            onStartDay1={() => { if (program.status === 'NOT_STARTED') return begin(); setSelected(completed ? 1 : currentDay); document.getElementById('flagship-days')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} />
        )}

        <div className="rounded-3xl bg-[#FFFCF3] border border-[#e3d2a8]/50 p-4 mb-6" data-testid="flagship-progress-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-[#a88a4c] uppercase tracking-[0.25em]">{guest ? 'Open to everyone' : 'Your progress'}</p>
            <span className="text-xs font-semibold text-[#0f1e33]" data-testid="flagship-progress-text">
              {guest ? 'Progress not tracked' : program.status === 'NOT_STARTED' ? 'Not started' : `${dayLabel} ${completed ? program.totalDays : program.currentDay} of ${program.totalDays} · ${program.percentComplete}%`}
            </span>
          </div>
          <div className="h-1.5 bg-[#efe3ce] rounded-full overflow-hidden mb-4">
            <div className="h-full bg-[#D6AD60] rounded-full transition-[width] duration-700" style={{ width: `${program.status === 'NOT_STARTED' ? 0 : program.percentComplete}%` }} />
          </div>
          {program.badgeEarned && (
            <p className="inline-flex items-center gap-1.5 text-xs text-[#5c8477] font-semibold mb-3" data-testid="flagship-badge-earned">
              <Award className="w-3.5 h-3.5" /> {program.badgeName} · completed {data.history.length} time{data.history.length === 1 ? '' : 's'}
            </p>
          )}
          {active && user && !isPreview && <ProgramReminderTimes slug={slug} key={program.attemptId} />}
          {data.welcome?.audioUrl && (active || completed) && !isPreview && (
            <button type="button" onClick={() => { setWelcome('replay'); trackProgramEvent(slug, 'program_welcome_replay'); }} className="mb-3 text-xs font-semibold text-[#a88a4c] inline-flex items-center gap-1.5" data-testid="flagship-welcome-replay">
              <Mic className="w-3.5 h-3.5" /> Hear Rowdy's Welcome Again
            </button>
          )}
          {program.status === 'NOT_STARTED' && (
            <button type="button" onClick={begin} disabled={busy} className="w-full h-12 rounded-full bg-[#0f1e33] text-[#f9f4ea] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#1e3a5f] transition-colors disabled:opacity-50" data-testid="flagship-begin">
              Begin Journey
            </button>
          )}
          {guest && (
            <Link to="/auth" className="mt-2 w-full h-10 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold text-[#a88a4c] hover:text-[#0f1e33] transition-colors" data-testid="flagship-guest-signin">
              <UserRound className="w-3.5 h-3.5" /> Sign in to track your progress, streak and badge
            </Link>
          )}
          {active && sel !== currentDay && (
            <button type="button" onClick={() => setSelected(currentDay)} className="w-full h-12 rounded-full bg-[#D6AD60] text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#c9a866] transition-colors" data-testid="flagship-resume">
              Resume {dayLabel} {currentDay}
            </button>
          )}
          <JourneyBuddies slug={slug} dayLabel={dayLabel} data={buddies} onChange={loadBuddies} />
          {program.badgeEarned && !isPreview && (
            <button type="button" onClick={() => setInvite(true)} className="w-full mb-2 h-11 rounded-full border border-[#e3d2a8]/60 text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.18em]" data-testid="flagship-invite">Invite a Friend</button>
          )}
          {program.badgeEarned && !isPreview && (
            <button type="button" onClick={() => setPostcard(true)} className="w-full mb-2 h-11 rounded-full border border-[#D6AD60] text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.18em]" data-testid="flagship-postcard">Share Your Journey</button>
          )}
          {completed && (
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setCelebrate(true)} className="h-12 rounded-full bg-[#D6AD60] text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.2em]" data-testid="flagship-view-badge">View your badge</button>
              <button type="button" onClick={begin} disabled={busy} className="h-12 rounded-full border border-[#0f1e33]/20 text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.2em] disabled:opacity-50" data-testid="flagship-restart">Start Again</button>
            </div>
          )}
        </div>

        <div id="flagship-days" className="scroll-mt-24" />
        <ProgramDayStrip days={days} selected={sel} currentDay={currentDay} completedDays={completedDays} unlockedAll={completed || !active} onSelect={setSelected} dayLabel={dayLabel} streak={streak} expanding={expanding} />
        {streakLine && active && <p className="text-xs text-[#e3d2a8] mt-1 inline-flex items-center gap-1.5" data-testid="flagship-streak-copy"><Sparkles className="w-3.5 h-3.5 text-[#D6AD60]" /> {streakLine}</p>}

        {day?.archived && (
          <div className="mt-4 rounded-3xl border border-[#e3d2a8]/40 bg-white/5 p-5 text-center" data-testid="flagship-day-archived">
            <Archive className="w-5 h-5 text-[#e3d2a8] mx-auto mb-2" />
            <p className="text-sm text-[#f9f4ea] font-semibold">{dayLabel} {sel} has been retired</p>
            <p className="text-xs text-[#e3d2a8]/80 mt-1">It's skipped automatically — nothing to do here. {dp?.completed ? 'Your earlier completion is kept.' : ''}</p>
          </div>
        )}
        {day && !day.archived && (
          <>
            <div className="mt-4 mb-1">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#e3d2a8]/80">{dayLabel} {sel}{day.phase ? ` · ${day.phase}` : ''}</p>
              <h2 className="text-2xl font-bold text-[#f9f4ea] leading-tight" style={SERIF} data-testid="flagship-day-title">{day.title}</h2>
              {locked && program.status !== 'COMPLETED' && !isPreview && (
                <p className="text-xs text-[#D6AD60] mt-1" data-testid="flagship-day-locked">
                  {active ? `Unlocks after ${dayLabel} ${sel - 1}. You can listen now; it counts once you get there.`
                    : user ? 'Listen or watch freely — begin the journey to track your progress.'
                      : 'Open to everyone — sign in to track your progress.'}
                </p>
              )}
              {dp?.completed && <p className="text-xs text-[#a8bfae] font-semibold mt-1" data-testid="flagship-day-complete">✓ {dayLabel} complete</p>}
            </div>
            {dp?.completed && !completed && (
              <div className="mt-3 rounded-2xl bg-[#D6AD60]/15 border border-[#D6AD60]/30 px-4 py-3" data-testid="flagship-day-encouragement">
                <p className="text-sm text-[#f9f4ea] leading-relaxed" style={SERIF}>{ENCOURAGEMENTS[(sel - 1) % ENCOURAGEMENTS.length]}</p>
                {sel < program.totalDays && <p className="text-[11px] text-[#e3d2a8]/80 mt-1">{dayLabel} {sel + 1} is ready whenever you are — today, or tomorrow when you come back.</p>}
              </div>
            )}

            {dp?.completed && active && sel < program.totalDays && sel < currentDay && (
              <button type="button" onClick={() => setSelected(sel + 1)} className="w-full mt-3 h-11 rounded-full bg-[#7a9b8e]/15 text-[#5c8477] text-xs font-semibold uppercase tracking-[0.18em] inline-flex items-center justify-center gap-1" data-testid="flagship-next-day">
                Continue to {dayLabel} {sel + 1} <ChevronRight className="w-4 h-4" />
              </button>
            )}

            <SectionHead icon={Sun} label="Today" />
            <div className="flex flex-col gap-3">
              <MeditationStep day={day} cover={cover} progress={dp} locked={locked} guest={guest} onComplete={(method) => act('meditation', 'complete', { method })} />
              <AffirmationStep day={day} progress={dp} locked={locked} guest={guest} onComplete={() => act('affirmation', 'complete')} />
              <ProgramJournalStep day={day} progress={dp} entry={entry} locked={locked} guest={guest} program={program} onSave={(text) => act('journal', 'complete', { text })} onSkip={() => act('journal', 'skip')} />
              <BreathingStep day={day} />
            </div>

            <div id="tonight" className="scroll-mt-24" />
            <SectionHead icon={Moon} label="Tonight" hint="Optional" />
            <EveningStep day={day} cover={cover} progress={dp} locked={locked} guest={guest} onComplete={() => act('evening', 'complete')} onSkip={() => act('evening', 'skip')} />

            <SectionHead icon={Users} label="Community" hint="Optional" />
            <ProgramCommunityShare program={program} day={day} journalText={entry?.content} locked={!user || isPreview} />

            {day.recommendedContentTitle && (
              <p className="mt-5 inline-flex items-center gap-2 text-xs text-[#e3d2a8]/80" data-testid="flagship-recommended"><Sparkles className="w-3.5 h-3.5 text-[#D6AD60]" /> Rowdy also recommends: <span className="font-semibold text-[#f9f4ea]">{day.recommendedContentTitle}</span></p>
            )}
          </>
        )}
      </div>

      {invite && <JourneyInvite program={program} onClose={() => setInvite(false)} />}
      {postcard && <JourneyPostcard program={program} completedAt={program.completedAt || lastCompletion?.completedAt} attemptId={lastCompletion?.attemptId || program.attemptId} onClose={() => setPostcard(false)} />}
      {welcome && data.welcome && (
        <ProgramWelcome program={program} welcome={data.welcome} replay={welcome === 'replay'} onBegin={() => { setWelcome(null); setSelected(currentDay || 1); }} />
      )}
      {celebrate && (
        <ProgramCompletion
          program={program}
          completedAt={program.completedAt || lastCompletion?.completedAt}
          completionNumber={lastCompletion?.completionNumber || program.completions}
          circleId={program.defaultCircleId}
          onClose={() => setCelebrate(false)}
          onRestart={begin}
        />
      )}
    </div>
  );
}
